import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { getJson } from "./getJson.js";
import {
  feedLimit,
  feedSessions,
  queryKeys,
  type ScreenParams,
  SIGNALS_DEFAULT_LIMIT,
} from "./query-keys.js";

export type { ScreenParams };
// Re-exported so every existing `from "./queries.js"` import keeps working:
// the definitions moved, the surface did not.
export { feedLimit, feedSessions, queryKeys, SIGNALS_DEFAULT_LIMIT };

export interface ScreenFactor {
  id: string;
  label: string;
  direction: string;
  status: "available" | "stale" | "unavailable";
  reason: string | null;
  staleSessions: number | null;
}

export interface ScreenScore {
  raw: number | null;
  percentile: number | null;
  sectorPercentile: number | null;
}

export interface ScreenRow {
  symbol: string;
  exchange: string;
  name: string;
  series: string;
  sector: string | null;
  close: number | null;
  scores: Record<string, ScreenScore>;
}

export interface ScreenMeta {
  rowCount: number;
  factorCount: number;
  contributingFactors: number;
  payloadBytes: number;
  generatedAt: string;
}

export interface ScreenResponse {
  sessionDate: string;
  factors: ScreenFactor[];
  rows: ScreenRow[];
  meta: ScreenMeta;
}

export interface FactorSummary {
  id: string;
  label: string;
  direction: string;
  neutralise: string | null;
  availableFrom: string | null;
  /** Plain-English data requirements, so a coverage floor is visible without the spec. */
  requires: string[];
}

/**
 * Mirrors `apps/api/src/query/outcomes.ts`.
 *
 * Hand-copied like the rest of this file's response types. Null is not zero
 * anywhere in here: a horizon that has not matured has `n: 0` and a null
 * median, and rendering that as 0% would read as "this signal returns
 * nothing" rather than "we do not know yet".
 */
export interface OutcomeTile {
  horizon: 5 | 21 | 63;
  n: number;
  wins: number;
  winRate: number;
  wilson: [number, number];
  medianReturn: number | null;
  medianNetReturn: number | null;
  paysAfterCosts: boolean;
  p25: number | null;
  p75: number | null;
  insufficient: boolean;
}

export interface OutcomePanel {
  signalType: string;
  total: number;
  bestEvidenced: 5 | 21 | 63 | null;
  horizons: OutcomeTile[];
}

export interface LabResponse {
  factorId: string;
  label: string;
  distribution: { bucket: number; count: number }[];
  spread: { sessionDate: string; grossBps: number; netBps: number }[];
  costBps: number;
  paysAfterCosts: boolean;
  coverage: { scored: number; nulls: number };
  outcomes: OutcomePanel[];
}

export function withParams(path: string, params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") search.set(key, value);
  }
  const query = search.toString();
  return query ? `${path}?${query}` : path;
}

export function useScreen(params: ScreenParams) {
  return useQuery({
    queryKey: queryKeys.screen(params),
    queryFn: () =>
      getJson<ScreenResponse>(
        withParams("/api/screen", {
          session: params.session,
          factors: params.factors?.join(","),
        }),
      ),
    // The ranking changes once a day, after the 20:00 scoring run.
    staleTime: 5 * 60_000,
  });
}

export function useFactors() {
  return useQuery({
    queryKey: queryKeys.factors(),
    queryFn: () => getJson<{ factors: FactorSummary[] }>("/api/factors"),
    // The registry changes when the build changes, not when a session does.
    staleTime: 60 * 60_000,
  });
}

export function useLab(factorId: string, session?: string) {
  return useQuery({
    queryKey: queryKeys.lab(factorId, session),
    queryFn: () => getJson<LabResponse>(withParams(`/api/lab/${factorId}`, { session })),
    enabled: factorId !== "",
    staleTime: 5 * 60_000,
    // Picking another factor changes the key, and without this both charts
    // unmount to "Loading…" and then remount two ResponsiveContainers, two
    // ResizeObservers, and two full axis sets. `/api/lab/vol_60d` took 3,492ms
    // in production, so that is three and a half seconds of blank panel per
    // click. Keeping the previous factor on screen makes it a background
    // refetch instead — the same treatment, for the same reason, that
    // `useSignals` gives the feed.
    placeholderData: keepPreviousData,
  });
}

// ---------------------------------------------------------------------------
// Task 29: symbol, signals, and health.
//
// The keys were reserved in `queryKeys` above when this file was created, so
// adding these three hooks changed no existing key and invalidated no cache.
// ---------------------------------------------------------------------------

export interface SymbolBar {
  sessionDate: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  deliveryPct: number | null;
  isAdjusted: boolean;
}

export interface SymbolAction {
  exDate: string;
  actionType: string;
  seq: number;
  ratioFrom: number | null;
  ratioTo: number | null;
  amount: number | null;
  /** Rupees over face value, for a rights issue. Zero is a par issue. */
  premium: number | null;
  announcedOn: string | null;
  /** The exchange's own words, which is the only way to explain a parse failure. */
  rawPurpose: string | null;
  parseStatus: string;
  parseNote: string | null;
}

/** Mirrors `apps/api/src/query/symbol.ts`. */
export interface FactorHistoryPoint {
  sessionDate: string;
  percentile: number | null;
  rawValue: number | null;
  zScore: number | null;
  sectorPercentile: number | null;
}

export interface SymbolSignal {
  id: string;
  sessionDate: string;
  occurredAt: string;
  signalType: string;
  /** The job that emitted the row. Not the holding period; see `horizon`. */
  family: string;
  /** How long the strategy means to hold. Null for a type no strategy owns. */
  horizon: TradeHorizon | null;
  /** True when the row restates evidence the page already lists. */
  derived: boolean;
  severity: string;
  payload: Record<string, unknown>;
  acknowledgedAt: string | null;
  /** The same sentence the feed and Telegram use. */
  explanation: SignalExplanation;
  /**
   * Entry, stop, target, and how far the live quote has travelled between
   * them, priced by the same `tradeMetricsOf` the feed uses. Null for a signal
   * that names no price — a rank crossing has no entry.
   */
  trade: SignalTrade | null;
}

export interface SymbolFiling {
  announcedAt: string;
  category: string;
  subject: string;
  attachmentUrl: string | null;
  bodyText: string | null;
  /** The body was sent, cut at the server's limit. */
  bodyTruncated: boolean;
  /** A body exists and was not sent, because this filing is older than the newest few. */
  bodyOmitted: boolean;
}

/**
 * Mirrors `TradeHorizon` in `@savi/core/signals/catalogue.ts`, for the reason
 * `SignalTrade` mirrors `TradeMetrics`: the web app does not depend on
 * `@savi/core`, which is a Node-shaped package.
 */
export type TradeHorizon = "session" | "swing" | "position";

export interface SignalExplanation {
  title: string;
  sentence: string;
  facts: { label: string; value: string }[];
}

export interface SymbolFundamental {
  periodEnd: string;
  statementType: string;
  basis: string;
  periodType: string;
  filedOn: string;
  revision: string;
  asReported: boolean;
  lineItems: Record<string, number | null>;
}

export interface SymbolResponse {
  symbol: string;
  exchange: string;
  name: string;
  series: string;
  sector: string | null;
  industry: string | null;
  isin: string | null;
  faceValue: number | null;
  faceValueSeenOn: string | null;
  listingDate: string | null;
  delistingDate: string | null;
  /** The indexes holding this name at the most recent rebalance. */
  indexes: string[];
  bars: SymbolBar[];
  actions: SymbolAction[];
  signals: SymbolSignal[];
  factorHistory: Record<string, FactorHistoryPoint[]>;
  filings: SymbolFiling[];
  events: {
    eventType: string;
    occurredOn: string;
    confidence: number;
    classifierVersion: string;
    payload: unknown;
  }[];
  fundamentals: SymbolFundamental[];
  quote: { lastPrice: number; prevClose: number | null; fetchedAt: string } | null;
  /** Set when an unparsed corporate action blocks adjustment. */
  adjustmentBlocked: { from: string; reason: string } | null;
  meta: { payloadBytes: number; generatedAt: string };
}

export interface SignalView {
  id: string;
  exchange: string;
  symbol: string;
  sessionDate: string;
  occurredAt: string;
  signalType: string;
  /** The job that emitted the row. Not the holding period; see `horizon`. */
  family: string;
  /**
   * How long the strategy means to hold: session, swing, or position.
   *
   * `family` has never answered this. `prophet_swing` writes
   * `family: "intraday"` because the hourly runner evaluates it, and it holds
   * for days — so a table with only Family shows a swing long above a gap fade
   * that dies at the close, and the pair reads as one strategy contradicting
   * itself. Null for a type no strategy owns; the column renders a dash.
   */
  horizon: TradeHorizon | null;
  /**
   * True when the row restates evidence the feed already carries.
   *
   * Only confluence, which is scored from the votes of strategies that each
   * wrote their own row. Counting both counts one observation twice.
   */
  derived: boolean;
  severity: string;
  payload: Record<string, unknown>;
  acknowledgedAt: string | null;
  /** The same sentence Telegram sends, built once in `@savi/core`. */
  explanation: SignalExplanation;
  /** The last traded price behind every "since" number in the table. */
  quote: { lastPrice: number; fetchedAt: string } | null;
  /**
   * What the name is worth, in rupees, as of the last scoring run.
   *
   * Null for the 39% of symbols the pipeline cannot size, and for the handful
   * whose filings put the cap past believing.
   * Both read as an em dash in the column and both answer the Size filter's
   * "Unknown" chip, because both mean the same thing.
   *
   * Rupees rather than crore on the wire: the column divides once at the last
   * moment, and a unit conversion in transit is a factor-of-1e7 bug waiting
   * for the first person who forgets it happened.
   */
  marketCap: number | null;
  /**
   * Mirrors `TradeMetrics` in `@savi/core/signals/trade.ts`. Duplicated rather
   * than imported: the web app deliberately does not depend on `@savi/core`,
   * which is a Node-shaped package (`format.ts:47`).
   */
  trade: SignalTrade | null;
}

export interface SignalTrade {
  plan: {
    side: "long" | "short";
    /** The print the signal fired on. */
    entry: number;
    stop: number | null;
    target: number | null;
    atr: number | null;
  };
  /** Stop distance over entry: the per-share risk. */
  riskPct: number | null;
  rewardRisk: number | null;
  atrPct: number | null;
  /** Null when nothing quotes the name, not when the trade is flat. */
  progress: {
    /** Signed to the side: positive is in the trade's favour. */
    returnPct: number;
    rMultiple: number | null;
    /** 0 at entry, 100 at target. */
    targetProgressPct: number | null;
    /** Read off one price, not the path. A stop touched at 13:00 and recovered reads "open". */
    status: "open" | "target" | "stopped";
  } | null;
}

export interface SignalsResponse {
  signals: SignalView[];
  /** Unread across everything the filters match, not just this page. */
  unreadCount: number;
  /** What the filters match in total. `signals.length` is capped by the limit. */
  total: number;
}

export interface SignalDetailResponse {
  signal: SignalView;
  context: {
    name: string;
    sector: string | null;
    bars: SymbolBar[];
    /** Other signals on this symbol inside the focus window. */
    nearby: SignalView[];
    /** What this signal type has been worth, or null when nothing is answered. */
    outcomes: OutcomePanel | null;
  };
}

export interface PipelineHealth {
  name: string;
  lastOkAt: string | null;
  lastSession: string | null;
  rowsLastRun: number;
  status: "ok" | "failed" | "never" | "running" | "disabled";
  error: string | null;
  staleSessions: number | null;
}

export interface HealthResponse {
  /**
   * The commit this build came from, or "unknown" when the image was built
   * without one. Mirrors `apps/api/src/routes/health-detail.ts`.
   */
  build: string;
  pipelines: PipelineHealth[];
  counts: Record<string, number>;
  kite: {
    valid: boolean;
    expiresAt: string | null;
    obtainedBy: string | null;
    unmappedSymbols: number;
  };
  review: { openItems: number; blockedSymbols: number };
  classifier: {
    version: string;
    precision: number | null;
    recall: number | null;
    evaluatedAt: string | null;
  };
  /** Mirrors `DataHealth` in `apps/api/src/routes/types.ts`. */
  data: {
    sessions: {
      sessionDate: string;
      symbols: number;
      hourlySymbols: number;
      hourlyBuckets: number;
    }[];
    invalidations: {
      pending: number;
      recent: {
        tableName: string;
        symbol: string;
        fromDate: string;
        toDate: string;
        reason: string;
        observedAt: string;
      }[];
    };
  };
}

/**
 * How much history the symbol payload has to carry.
 *
 * `GET /api/symbol/:symbol` defaults to two years so an ordinary visit does
 * not read five, but `ChartTimeframeControl` offers `5Y` and `Max` and slices
 * `bars` on the client — it does not refetch. Asking for less here would not
 * make those two buttons slower, it would make them wrong: they would quietly
 * draw two years and label it five.
 *
 * The honest fix is to refetch when the reader widens the timeframe, which is
 * a change to the chart rather than to this hook.
 */
const SYMBOL_HISTORY_YEARS = "5";

export function useSymbol(symbol: string) {
  return useQuery({
    queryKey: queryKeys.symbol(symbol),
    queryFn: () =>
      getJson<SymbolResponse>(
        withParams(`/api/symbol/${encodeURIComponent(symbol)}`, {
          years: SYMBOL_HISTORY_YEARS,
        }),
      ),
    staleTime: 5 * 60_000,
  });
}

export function useHealth() {
  return useQuery({
    queryKey: queryKeys.health(),
    queryFn: () => getJson<HealthResponse>("/api/health/pipelines"),
    // The page you open when a number looks wrong should not show you a
    // cached version of the number that looked wrong.
    staleTime: 0,
    refetchInterval: 60_000,
  });
}
